import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, FileText, Download } from 'lucide-react';
import { api } from '../lib/api';

const ACCENT = '#f97316';

interface JobStatus {
  job_id: string;
  status: string;
  file_name: string;
  target_product: string;
  target_entity: string;
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  skipped: number;
}

interface ImportError {
  row: number;
  field?: string;
  value?: string;
  message: string;
}

export default function MigrationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      api.get<JobStatus>(`/import/${id}/status`).catch(() => null),
      api.get<{ errors: ImportError[] }>(`/import/${id}/errors`).catch(() => ({ errors: [] })),
    ]).then(([s, e]) => {
      if (s) setStatus(s);
      setErrors(e?.errors || []);
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p style={{ color: '#6b7280' }}>Loading...</p>;
  if (!status) return <p style={{ color: '#ef4444' }}>Job not found</p>;

  const progressPct = status.total > 0 ? Math.round((status.processed / status.total) * 100) : 0;

  return (
    <div>
      <button onClick={() => navigate('/migrations')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#6b7280', marginBottom: '1rem', padding: 0, fontSize: '0.875rem' }}>
        <ArrowLeft size={16} /> Back to Migrations
      </button>

      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 1.5rem', color: '#111827' }}>
        <FileText size={20} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
        {status.file_name}
      </h2>

      {/* Progress */}
      <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
          <span style={{ fontWeight: 600 }}>{status.target_product} / {status.target_entity}</span>
          <span style={{ fontWeight: 500, color: status.status === 'completed' ? '#10b981' : status.status === 'failed' ? '#ef4444' : ACCENT }}>
            {status.status.toUpperCase()}
          </span>
        </div>

        <div style={{ height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden', marginBottom: '0.75rem' }}>
          <div style={{ height: '100%', width: `${progressPct}%`, backgroundColor: ACCENT, borderRadius: 4, transition: 'width 0.3s' }} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', textAlign: 'center' }}>
          <div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{status.total.toLocaleString()}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Total</div>
          </div>
          <div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#10b981' }}>{status.succeeded.toLocaleString()}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Succeeded</div>
          </div>
          <div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#ef4444' }}>{status.failed.toLocaleString()}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Failed</div>
          </div>
          <div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#6b7280' }}>{status.skipped.toLocaleString()}</div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Skipped</div>
          </div>
        </div>
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '1.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Errors ({errors.length})</h3>
            <button style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', backgroundColor: '#fff', cursor: 'pointer', fontSize: '0.8rem' }}>
              <Download size={14} /> Export CSV
            </button>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 600, color: '#6b7280' }}>Row</th>
                <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 600, color: '#6b7280' }}>Error</th>
              </tr>
            </thead>
            <tbody>
              {errors.map((err, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '0.5rem' }}>{err.row}</td>
                  <td style={{ padding: '0.5rem', color: '#ef4444' }}>{err.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
